import { useState, useEffect, useCallback, useRef } from "react";
import api from "../../services/api";
import socketService from "../../services/socketService";
import { useAuth } from "../../context/AuthContext";
import clsx from "clsx";

const STATUS_CONFIG = {
  available: { label: "Available", color: "bg-emerald-500", textColor: "text-emerald-700", bgLight: "bg-emerald-50" },
  on_call: { label: "On Call", color: "bg-blue-500", textColor: "text-blue-700", bgLight: "bg-blue-50" },
  wrap_up: { label: "Wrap Up", color: "bg-amber-500", textColor: "text-amber-700", bgLight: "bg-amber-50" },
  break: { label: "Break", color: "bg-orange-500", textColor: "text-orange-700", bgLight: "bg-orange-50" },
  offline: { label: "Offline", color: "bg-surface-400", textColor: "text-surface-600", bgLight: "bg-surface-100" },
};

const SELECTABLE_MAP = {
  offline: ["available"],
  available: ["break", "offline"],
  break: ["available", "offline"],
  on_call: [],
  wrap_up: ["available", "break"],
};

function extractStatus(res) {
  return res?.data?.status || res?.status || null;
}

export default function AgentStatusSelector({ compact = false }) {
  const { user } = useAuth();
  const [status, setStatus] = useState("offline");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get("/agent-status/me");
      const s = extractStatus(res);
      if (s && STATUS_CONFIG[s]) setStatus(s);
    } catch (err) {
      console.warn("[AgentStatus] Failed to fetch status:", err?.message || err);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const handler = (data) => {
      if (data.userId === (user?._id || user?.id)) {
        if (data.status && STATUS_CONFIG[data.status]) setStatus(data.status);
      }
    };
    socketService.on("agent:status-changed", handler);
    return () => socketService.off("agent:status-changed", handler);
  }, [user]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleSelect = async (newStatus) => {
    setOpen(false);
    const prevStatus = status;
    setStatus(newStatus);
    setLoading(true);
    try {
      const res = await api.put("/agent-status/me", { status: newStatus });
      const confirmed = extractStatus(res);
      if (confirmed && STATUS_CONFIG[confirmed]) setStatus(confirmed);
    } catch (err) {
      console.warn("[AgentStatus] Failed to update status:", err?.message || err);
      setStatus(prevStatus);
    } finally {
      setLoading(false);
    }
  };

  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.offline;
  const isAutoManaged = status === "on_call" || status === "wrap_up";
  const selectableStatuses = (SELECTABLE_MAP[status] || []).filter((s) => s !== status);

  if (compact) {
    return (
      <div ref={ref} className="relative">
        <button
          onClick={() => !isAutoManaged && selectableStatuses.length > 0 && setOpen(!open)}
          disabled={loading}
          className={clsx(
            "flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors",
            cfg.bgLight, cfg.textColor,
            isAutoManaged || selectableStatuses.length === 0 ? "cursor-default" : "hover:opacity-80 cursor-pointer",
            loading && "opacity-60"
          )}
        >
          <div className={clsx("w-2 h-2 rounded-full", cfg.color, status === "on_call" && "animate-pulse")} />
          {cfg.label}
        </button>
        {open && selectableStatuses.length > 0 && (
          <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-xl border border-surface-200 py-1 z-50 min-w-[140px]">
            {selectableStatuses.map((s) => {
              const c = STATUS_CONFIG[s];
              return (
                <button
                  key={s}
                  onClick={() => handleSelect(s)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-surface-50 transition-colors"
                >
                  <div className={clsx("w-2 h-2 rounded-full", c.color)} />
                  {c.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => !isAutoManaged && selectableStatuses.length > 0 && setOpen(!open)}
        disabled={loading}
        className={clsx(
          "flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-all",
          cfg.bgLight, cfg.textColor, "border-current/20",
          isAutoManaged || selectableStatuses.length === 0 ? "cursor-default" : "hover:shadow-sm cursor-pointer",
          loading && "opacity-60"
        )}
      >
        <div className={clsx("w-2.5 h-2.5 rounded-full", cfg.color, status === "on_call" && "animate-pulse")} />
        {cfg.label}
      </button>
      {open && selectableStatuses.length > 0 && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-xl border border-surface-200 py-1 z-50 min-w-[160px]">
          {selectableStatuses.map((s) => {
            const c = STATUS_CONFIG[s];
            return (
              <button
                key={s}
                onClick={() => handleSelect(s)}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-surface-50 transition-colors"
              >
                <div className={clsx("w-2.5 h-2.5 rounded-full", c.color)} />
                {c.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
